
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TimeSlot } from '@/utils/timeSlotUtils';
import { Loader2 } from 'lucide-react';

const bookingFormSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  phone: z.string().optional(),
  meetingType: z.string().min(1, {
    message: "Please select a meeting type.",
  }),
  notes: z.string().optional(),
});

export type BookingFormValues = z.infer<typeof bookingFormSchema>;

interface BookingFormProps {
  selectedDate: Date | undefined;
  selectedTimeSlot: TimeSlot | null;
  meetingTypes: Record<string, boolean>;
  selectedMeetingType: string;
  onMeetingTypeChange: (type: string) => void;
  onSubmit: (data: BookingFormValues) => void;
  isSubmitting?: boolean;
}

const BookingForm: React.FC<BookingFormProps> = ({
  selectedDate,
  selectedTimeSlot,
  meetingTypes,
  selectedMeetingType,
  onMeetingTypeChange,
  onSubmit,
  isSubmitting = false
}) => {
  // Get only enabled meeting types
  const enabledMeetingTypes = Object.keys(meetingTypes).filter(type => meetingTypes[type]);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      meetingType: selectedMeetingType || (enabledMeetingTypes.length > 0 ? enabledMeetingTypes[0] : ""),
      notes: "",
    },
  });

  React.useEffect(() => {
    if (selectedMeetingType) {
      form.setValue('meetingType', selectedMeetingType);
    }
  }, [selectedMeetingType, form]);

  const handleSubmit = (data: BookingFormValues) => {
    if (!selectedDate || !selectedTimeSlot) {
      toast.error("Please select a date and time for your appointment");
      return;
    }
    
    // Pass the meeting type to the parent component
    onMeetingTypeChange(data.meetingType);
    onSubmit(data);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-lg font-medium mb-4">Your Information</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Your name" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="your@email.com" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone (optional)</FormLabel>
                <FormControl>
                  <Input 
                    type="tel" 
                    placeholder="Your phone number" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason for appointment (optional)</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Briefly describe the reason for your appointment" 
                    rows={3}
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={!selectedDate || !selectedTimeSlot || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Schedule Appointment"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default BookingForm;
